/**
 * Forward Kinematics — Skeleton + Frame → world joint positions
 *
 * Follows the standard CMU ASF/AMC FK algorithm:
 *   rotation[bone] = parent.rotation * C * R * C_inv
 *   position[bone] = parent.position + length * rotation[bone] * direction
 *
 * Where:
 *   C = bone axis rotation (local→global, from ASF axis field)
 *   R = frame rotation (from AMC, in bone's local frame, mapped by DOF)
 *   C_inv = inverse of C
 *   direction = bone direction in world coordinates (from ASF)
 *
 * Rotation is accumulated as a pure rotation matrix (no translation).
 * Position is computed separately via vector addition.
 *
 * Reference: CalciferZh/AMCParser, Facebook Research fairmotion
 */

import type { Skeleton } from './asfParser';
import type { Frame } from './amcParser';
import type { Three } from '../types';

export interface JointPosition {
  x: number;
  y: number;
  z: number;
}

export type JointPositions = Map<string, JointPosition>;

const DEG2RAD = Math.PI / 180;

/**
 * ASF axis orders use extrinsic (static) convention. Three.js Euler uses intrinsic.
 * Intrinsic ZYX = Extrinsic XYZ, so reverse the order string.
 */
function asfToThreeEulerOrder(asfOrder: string): string {
  return asfOrder.toUpperCase().split('').reverse().join('');
}

export function computeFK(
  three: Three,
  skeleton: Skeleton,
  frame: Frame,
  scale: number
): JointPositions {
  const positions: JointPositions = new Map();
  const ls = skeleton.lengthScale;

  // Root: position from AMC, rotation = C * R * C_inv (C is identity for root axis 0,0,0)
  const rootData = frame.get('root');
  let rootPos = new three.Vector3(0, 0, 0);
  const rootRotation = new three.Matrix4(); // identity

  if (rootData && rootData.length >= 6) {
    rootPos = new three.Vector3(
      rootData[0] * ls * scale,
      rootData[1] * ls * scale,
      rootData[2] * ls * scale
    );

    const euler = new three.Euler(
      rootData[3] * DEG2RAD,
      rootData[4] * DEG2RAD,
      rootData[5] * DEG2RAD,
      'ZYX' as any
    );
    rootRotation.makeRotationFromEuler(euler);
  }

  positions.set('root', { x: rootPos.x, y: rootPos.y, z: rootPos.z });

  // DFS traversal
  const children = skeleton.hierarchy.get('root');
  if (children) {
    for (const child of children) {
      traverseBone(three, skeleton, frame, scale, child, rootRotation, rootPos, positions);
    }
  }

  return positions;
}

function traverseBone(
  three: Three,
  skeleton: Skeleton,
  frame: Frame,
  scale: number,
  boneName: string,
  parentRotation: InstanceType<Three['Matrix4']>,
  parentPos: InstanceType<Three['Vector3']>,
  positions: JointPositions
): void {
  const bone = skeleton.bones.get(boneName);
  if (!bone) return;

  const ls = skeleton.lengthScale;

  // C: bone axis rotation (local→global)
  const C = new three.Matrix4();
  const eulerOrder = asfToThreeEulerOrder(bone.axisOrder);
  const cEuler = new three.Euler(
    bone.axis[0] * DEG2RAD,
    bone.axis[1] * DEG2RAD,
    bone.axis[2] * DEG2RAD,
    eulerOrder as any
  );
  C.makeRotationFromEuler(cEuler);

  // C_inv: global→local
  const C_inv = C.clone().invert();

  // R: frame rotation from AMC data mapped by DOF
  const R = new three.Matrix4();
  const frameData = frame.get(boneName);
  if (frameData && bone.dof.length > 0) {
    const angles: [number, number, number] = [0, 0, 0];
    let di = 0;
    for (const d of bone.dof) {
      const val = (frameData[di] ?? 0) * DEG2RAD;
      switch (d) {
        case 'rx': angles[0] = val; break;
        case 'ry': angles[1] = val; break;
        case 'rz': angles[2] = val; break;
      }
      di++;
    }
    const rEuler = new three.Euler(angles[0], angles[1], angles[2], eulerOrder as any);
    R.makeRotationFromEuler(rEuler);
  }

  // Accumulated rotation = parentRotation * C * R * C_inv
  const boneRotation = new three.Matrix4();
  boneRotation.copy(parentRotation);
  boneRotation.multiply(C);
  boneRotation.multiply(R);
  boneRotation.multiply(C_inv);

  // Position = parentPos + length * boneRotation * direction
  const dir = new three.Vector3(bone.direction[0], bone.direction[1], bone.direction[2]);
  dir.applyMatrix4(boneRotation);
  dir.multiplyScalar(bone.length * ls * scale);
  const bonePos = parentPos.clone().add(dir);

  positions.set(boneName, { x: bonePos.x, y: bonePos.y, z: bonePos.z });

  // Recurse children
  const children = skeleton.hierarchy.get(boneName);
  if (children) {
    for (const child of children) {
      traverseBone(three, skeleton, frame, scale, child, boneRotation, bonePos, positions);
    }
  }
}
